import { useState } from "react";
import { createPortal } from "react-dom";
import {
  X,
  Plus,
  ArrowRight,
  LayoutGrid,
  Sparkles,
  Briefcase,
  ShoppingBag,
  CalendarDays,
  Tag,
} from "lucide-react";
import { CATEGORIES, TEMPLATES, PageTemplate, TemplateCategory } from "./templates";
import PagePreview from "./PagePreview";

interface Props {
  open: boolean;
  onClose: () => void;
  onPick: (template: PageTemplate | null) => void; // null = start from scratch
}

const CAT_ICON: Record<"all" | TemplateCategory, typeof LayoutGrid> = {
  all: LayoutGrid,
  creator: Sparkles,
  business: Briefcase,
  shop: ShoppingBag,
  events: CalendarDays,
  promotions: Tag,
};

const countFor = (key: "all" | TemplateCategory) =>
  key === "all" ? TEMPLATES.length : TEMPLATES.filter((t) => t.category === key).length;

const TemplatePicker = ({ open, onClose, onPick }: Props) => {
  const [cat, setCat] = useState<"all" | TemplateCategory>("all");
  if (!open) return null;

  const list = cat === "all" ? TEMPLATES : TEMPLATES.filter((t) => t.category === cat);

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-ink-900/50 backdrop-blur-sm p-4 sm:p-8 anim-fade overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="anim-scale bg-white rounded-3xl shadow-2xl ring-1 ring-ink-200/60 w-full max-w-6xl overflow-hidden my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative px-7 pt-7 pb-5 border-b border-ink-100">
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-brand-600">
            <Sparkles size={12} /> Templates
          </div>
          <h2 className="font-display text-2xl font-bold text-ink-900 tracking-tight mt-1.5">
            Choose a starting point
          </h2>
          <p className="text-sm text-ink-500 mt-1">
            Pick a layout built for your use case — you can change the content, colors, and fonts later.
          </p>
          <button
            onClick={onClose}
            aria-label="Close"
            className="absolute top-6 right-6 text-ink-400 hover:text-ink-700 hover:bg-ink-100 w-9 h-9 rounded-lg flex items-center justify-center transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex flex-col sm:flex-row">
          {/* Sidebar */}
          <div className="sm:w-60 shrink-0 p-5 sm:border-r border-ink-100 space-y-5">
            <button
              onClick={() => onPick(null)}
              className="group w-full inline-flex items-center gap-2.5 h-11 px-3.5 rounded-xl border-2 border-dashed border-brand-300 text-brand-700 text-sm font-semibold hover:bg-brand-50 hover:border-brand-400 transition-colors"
            >
              <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-brand-100 text-brand-600 group-hover:bg-brand-200 transition-colors">
                <Plus size={14} strokeWidth={2.5} />
              </span>
              Start from scratch
            </button>

            <div>
              <div className="text-[11px] uppercase tracking-wider text-ink-400 font-semibold mb-2 px-1">
                Browse by use case
              </div>
              <div className="flex sm:flex-col gap-1 overflow-x-auto">
                {CATEGORIES.map((c) => {
                  const Icon = CAT_ICON[c.key];
                  const active = cat === c.key;
                  return (
                    <button
                      key={c.key}
                      onClick={() => setCat(c.key)}
                      className={`flex items-center gap-2.5 whitespace-nowrap px-3 h-10 rounded-lg text-sm font-medium transition-colors ${
                        active ? "bg-brand-50 text-brand-700" : "text-ink-600 hover:bg-ink-100/70"
                      }`}
                    >
                      <Icon size={15} className={active ? "text-brand-600" : "text-ink-400"} />
                      <span className="text-left">{c.label}</span>
                      <span
                        className={`ml-auto hidden sm:inline-flex items-center justify-center min-w-[20px] h-5 px-1 rounded-md text-[11px] font-semibold ${
                          active ? "bg-brand-100 text-brand-700" : "bg-ink-100 text-ink-500"
                        }`}
                      >
                        {countFor(c.key)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Templates grid */}
          <div className="flex-1 p-5 max-h-[72vh] overflow-y-auto">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {list.map((t) => (
                <button
                  key={t.id}
                  onClick={() => onPick(t)}
                  className="group text-left rounded-2xl border border-ink-200/70 bg-white overflow-hidden hover:border-brand-400 hover:shadow-card-hover hover:-translate-y-0.5 transition-all duration-200"
                >
                  <div className="relative h-72 overflow-hidden bg-white pointer-events-none">
                    <PagePreview
                      title={t.data.title}
                      bio={t.data.bio}
                      avatarColor={t.data.avatar_color}
                      avatarImage={t.data.avatar_image}
                      blocks={t.data.blocks}
                      socials={t.data.socials}
                      design={t.data.design}
                    />
                    {/* Hover CTA overlay */}
                    <div className="absolute inset-0 flex items-end justify-center bg-gradient-to-t from-ink-900/40 via-ink-900/0 to-ink-900/0 opacity-0 group-hover:opacity-100 transition-opacity">
                      <span className="mb-4 inline-flex items-center gap-1.5 h-9 px-4 rounded-xl bg-white text-ink-900 text-sm font-semibold shadow-lg translate-y-2 group-hover:translate-y-0 transition-transform">
                        Use this template <ArrowRight size={15} strokeWidth={2.25} />
                      </span>
                    </div>
                  </div>
                  <div className="px-3.5 py-3 border-t border-ink-100">
                    <div className="text-sm font-semibold text-ink-900">{t.name}</div>
                    <div className="text-xs text-ink-500 mt-0.5 leading-snug">{t.blurb}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default TemplatePicker;
